import { useEffect, useRef, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { UploadSimple } from "@phosphor-icons/react";
import { Button } from "./ui/button";

const FAULTS = [["pending", "Pending review"], ["not_fault", "Not their fault"], ["fault", "Caregiver fault"]];
const PAY_DECISIONS = [["pending", "Pay: pending"], ["approved", "Pay: approved"], ["denied", "Pay: denied"]];
const MISS_CLASSES = [["", "Classify…"], ["compliant", "Compliant miss (tech/reported)"], ["non_compliant", "Non-compliant (forgot)"]];
const PAY_CLS = {
  pending: "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]",
  approved: "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]",
  denied: "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]",
};
const FAULT_CLS = {
  pending: "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]",
  not_fault: "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]",
  fault: "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]",
};

export function MissedPunches() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [approver, setApprover] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [mgrFor, setMgrFor] = useState(null);
  const [mgr, setMgr] = useState({});
  const fileRef = useRef(null);

  const load = () => api.get("/missed-punch").then((r) => setItems(r.data)).catch((e) => { toast.error(formatApiError(e)); setItems([]); });
  useEffect(() => {
    load();
    api.get("/missed-punch/settings").then((r) => setApprover(r.data.approver)).catch(() => {});
    api.get("/users").then((r) => {
      const list = Array.isArray(r.data) ? r.data : r.data.users || [];
      setAdmins(list.filter((u) => u.role === "admin"));
    }).catch(() => {});
  }, []);

  const saveApprover = async (email) => {
    try {
      await api.put("/missed-punch/settings", { approver_email: email });
      setApprover(email ? { email, name: admins.find((a) => a.email === email)?.name || email } : null);
      toast.success(email ? "Approver saved — they get an email for every new form" : "Approver cleared — all admins get notified");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openMgr = (m) => {
    setMgrFor(m.id);
    setMgr(m.manager_section || { reason_for_note: "", resolution: "", secondary_signer_name: "", secondary_sign_date: "", secondary_signer_phone: "", verbal_verified_by: "" });
  };

  const saveMgr = async (m) => {
    setBusy(true);
    try {
      await api.put(`/missed-punch/${m.id}`, { manager_section: mgr });
      setItems((list) => list.map((x) => (x.id === m.id ? { ...x, manager_section: mgr } : x)));
      setMgrFor(null);
      toast.success("Manager's section saved");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const logCall = async (m) => {
    const outcome = window.prompt(`Call outcome with ${m.client_name || "the client"} — type one: confirmed / no_answer / denied`, "confirmed");
    if (outcome === null) return;
    const o = outcome.trim().toLowerCase().replace(/ /g, "_");
    if (!["confirmed", "denied", "no_answer"].includes(o)) { toast.error("Outcome must be confirmed, denied, or no_answer"); return; }
    const note = window.prompt("Call note (optional — who you spoke with, what they said):", "") ?? "";
    try {
      const v = { method: "client_call", outcome: o, note };
      await api.put(`/missed-punch/${m.id}`, { verification: v });
      setItems((list) => list.map((x) => (x.id === m.id ? { ...x, verification: v, verified_with_client: o === "confirmed" || x.verified_with_client } : x)));
      toast.success(o === "confirmed" ? "Shift verified by client call — you can approve now" : `Call logged: ${o.replace("_", " ")}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendPortal = async (m) => {
    try {
      const { data } = await api.post(`/missed-punch/${m.id}/portal-request`);
      setItems((list) => list.map((x) => (x.id === m.id ? { ...x, portal_request: { sent_at: new Date().toISOString(), client_id: "sent" } } : x)));
      toast.success(`Confirmation request is now on ${data.client}'s portal`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    try {
      const r = await api.post("/missed-punch/upload", fd);
      toast.success(`Imported ${r.data.imported} missed punch form(s)`);
      if (r.data.unmatched?.length) toast.warning(`No employee match: ${r.data.unmatched.join(", ")}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const setFault = async (m, fault) => {
    try {
      await api.put(`/missed-punch/${m.id}`, { fault });
      setItems((list) => list.map((x) => (x.id === m.id ? { ...x, fault } : x)));
      toast.success(`${m.employee_name || "Form"} marked: ${FAULTS.find(([k]) => k === fault)?.[1]}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const review = async (m, patch) => {
    try {
      await api.put(`/missed-punch/${m.id}`, patch);
      setItems((list) => list.map((x) => (x.id === m.id ? { ...x, ...patch } : x)));
      toast.success("Review saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="missed-punches-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-stone-500">
          Missed punch forms document EVV exceptions. Every form needs <span className="font-medium text-stone-700">client verification before approval</span> — call the client (preferred) or push a confirmation to their portal, then approve.
        </p>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-stone-500">
            Approver
            <select value={approver?.email || ""} onChange={(e) => saveApprover(e.target.value)}
              className="h-8 rounded-md border border-stone-300 bg-white px-1.5 text-xs font-medium text-stone-700" data-testid="mp-approver-select">
              <option value="">Any admin</option>
              {admins.map((a) => <option key={a.email} value={a.email}>{a.name || a.email}</option>)}
            </select>
          </label>
          <input ref={fileRef} type="file" accept="application/pdf" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="mp-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="mp-upload-button">
            <UploadSimple size={15} className="mr-1.5" /> {busy ? "Importing…" : "Upload missed punch PDFs"}
          </Button>
        </div>
      </div>

      {items === null ? (
        <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      ) : items.length === 0 ? (
        <p className="rounded-lg border border-stone-200 bg-white p-6 text-center text-sm text-stone-400" data-testid="mp-empty">No missed punch forms yet — upload PDFs exported from JotForm.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="mp-table">
          <table className="w-full min-w-[1000px] text-left text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-secondary/60 text-xs uppercase tracking-wide text-stone-500">
                <th className="px-4 py-3 font-medium">Employee</th>
                <th className="px-3 py-3 font-medium">Work date</th>
                <th className="px-3 py-3 font-medium">Shift</th>
                <th className="px-3 py-3 font-medium">Reason</th>
                <th className="px-3 py-3 font-medium">Client</th>
                <th className="px-3 py-3 font-medium">Signatures</th>
                <th className="px-3 py-3 font-medium">Verified w/ client</th>
                <th className="px-3 py-3 font-medium">Approval</th>
                <th className="px-3 py-3 font-medium">Pay decision</th>
                <th className="px-3 py-3 font-medium">Fault</th>
                <th className="px-3 py-3 font-medium">Compliance</th>
                <th className="px-3 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((m, i) => (
                <Fragment key={m.id}>
                <tr className="border-b border-stone-100 last:border-0 align-middle" data-testid={`mp-row-${i}`}>
                  <td className="px-4 py-2.5">
                    {m.uid ? (
                      <Link to={`/team/${m.uid}`} className="font-medium text-stone-900 hover:text-primary hover:underline" data-testid={`mp-employee-${i}`}>{m.employee_name}</Link>
                    ) : (
                      <span className="font-medium text-[#C0392B]" data-testid={`mp-employee-${i}`}>{m.employee_name || m.filename} <span className="text-[10px]">(no match)</span></span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-stone-600">{m.work_date || "—"}</td>
                  <td className="px-3 py-2.5 text-xs text-stone-600">{m.shift_start}–{m.shift_end}</td>
                  <td className="px-3 py-2.5 text-xs text-stone-600">{m.reason_details || "—"}</td>
                  <td className="px-3 py-2.5 text-xs text-stone-600">{m.client_name || "—"}</td>
                  <td className="px-3 py-2.5 text-xs" data-testid={`mp-sigs-${i}`}>
                    {m.source === "app" ? (
                      <span className="space-x-1.5">
                        <span className={m.employee_signature ? "font-medium text-[#1e8449]" : "font-semibold text-[#C0392B]"}>Employee {m.employee_signature ? "✓" : "✗"}</span>
                        <span className={m.client_signed ? "font-medium text-[#1e8449]" : "font-semibold text-[#C0392B]"}>Client {m.client_signed ? "✓" : "✗"}</span>
                      </span>
                    ) : (
                      <span className="text-stone-400">on PDF</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5" data-testid={`mp-verified-${i}`}>
                    {m.verification ? (
                      <span className="text-xs">
                        <span className={m.verification.outcome === "confirmed" ? "font-medium text-[#1e8449]" : "font-semibold text-[#C0392B]"}>
                          {m.verification.outcome === "confirmed" ? "Verified" : m.verification.outcome === "denied" ? "Client denied" : "No answer"}
                        </span>
                        <span className="block text-[10px] text-stone-400">
                          {m.verification.method === "client_call" ? "by client call" : m.verification.method === "client_portal" ? "via client portal" : "other"} · {String(m.verification.at || "").slice(0, 10)}
                          {m.verification.note ? ` · ${m.verification.note.slice(0, 40)}` : ""}
                        </span>
                      </span>
                    ) : m.portal_request?.sent_at ? (
                      <span className="text-xs text-stone-500">Portal request sent {String(m.portal_request.sent_at).slice(0, 10)}<span className="block text-[10px] text-stone-400">waiting for client</span></span>
                    ) : (
                      <span className="flex flex-col gap-1">
                        <button onClick={() => logCall(m)} className="rounded-full border border-stone-300 px-2 py-0.5 text-[11px] font-medium text-stone-700 hover:bg-stone-900 hover:text-white" data-testid={`mp-log-call-${i}`}>Log client call</button>
                        <button onClick={() => sendPortal(m)} className="rounded-full border border-stone-300 px-2 py-0.5 text-[11px] font-medium text-stone-700 hover:bg-stone-900 hover:text-white" data-testid={`mp-portal-${i}`}>Send to client portal</button>
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <select value={(m.approval_status || "Pending review") === "Approved" ? "Approved" : (m.approval_status || "").startsWith("Not") ? "Not approved" : "Pending review"}
                      onChange={(e) => review(m, { approval_status: e.target.value })}
                      className="h-8 rounded-md border border-stone-300 bg-white px-1.5 text-xs font-medium text-stone-700" data-testid={`mp-approval-${i}`}>
                      {["Pending review", "Approved", "Not approved"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5">
                    <select value={m.pay_decision || "pending"} onChange={(e) => review(m, { pay_decision: e.target.value })}
                      className={`h-8 rounded-md border px-1.5 text-xs font-medium ${PAY_CLS[m.pay_decision || "pending"]}`} data-testid={`mp-pay-${i}`}>
                      {PAY_DECISIONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5">
                    <select value={m.fault} onChange={(e) => setFault(m, e.target.value)}
                      className={`h-8 rounded-md border px-1.5 text-xs font-medium ${FAULT_CLS[m.fault] || FAULT_CLS.pending}`} data-testid={`mp-fault-${i}`}>
                      {FAULTS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5">
                    <select value={m.miss_classification || ""} onChange={(e) => review(m, { miss_classification: e.target.value })}
                      className="h-8 rounded-md border border-stone-300 bg-white px-1.5 text-xs font-medium text-stone-700" data-testid={`mp-class-${i}`}>
                      {MISS_CLASSES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5">
                  <div className="flex flex-col items-start gap-1">
                    {m.source === "app" ? (
                      <span className="text-xs text-stone-400" data-testid={`mp-view-${i}`}>in-app form</span>
                    ) : (
                      <a href={`${api.defaults.baseURL}/missed-punch/${m.id}/file`} target="_blank" rel="noreferrer"
                        className="text-xs font-medium text-primary hover:underline" data-testid={`mp-view-${i}`}>View PDF</a>
                    )}
                    <button onClick={() => (mgrFor === m.id ? setMgrFor(null) : openMgr(m))}
                      className="text-xs font-medium text-stone-500 hover:text-primary hover:underline" data-testid={`mp-manager-${i}`}>
                      {m.manager_section ? "Manager's section ✓" : "Manager's section"}
                    </button>
                  </div>
                </td>
              </tr>
              {mgrFor === m.id && (
                <tr data-testid={`mp-manager-panel-${i}`}>
                  <td colSpan={9} className="bg-accent/40 px-4 py-3">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-500">Manager's Section</p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <input value={mgr.reason_for_note || ""} onChange={(e) => setMgr((f) => ({ ...f, reason_for_note: e.target.value }))}
                        placeholder="Reason for note" className="h-9 rounded-md border border-stone-300 bg-white px-2.5 text-xs" data-testid={`mgr-reason-${i}`} />
                      <input value={mgr.resolution || ""} onChange={(e) => setMgr((f) => ({ ...f, resolution: e.target.value }))}
                        placeholder="Details and resolution" className="h-9 rounded-md border border-stone-300 bg-white px-2.5 text-xs" data-testid={`mgr-resolution-${i}`} />
                      <input value={mgr.secondary_signer_name || ""} onChange={(e) => setMgr((f) => ({ ...f, secondary_signer_name: e.target.value }))}
                        placeholder="Secondary signer name" className="h-9 rounded-md border border-stone-300 bg-white px-2.5 text-xs" data-testid={`mgr-signer-${i}`} />
                      <input type="date" value={mgr.secondary_sign_date || ""} onChange={(e) => setMgr((f) => ({ ...f, secondary_sign_date: e.target.value }))}
                        className="h-9 rounded-md border border-stone-300 bg-white px-2.5 text-xs" data-testid={`mgr-sign-date-${i}`} />
                      <input value={mgr.secondary_signer_phone || ""} onChange={(e) => setMgr((f) => ({ ...f, secondary_signer_phone: e.target.value }))}
                        placeholder="Secondary signer phone (we call to verify)" className="h-9 rounded-md border border-stone-300 bg-white px-2.5 text-xs" data-testid={`mgr-signer-phone-${i}`} />
                      <input value={mgr.verbal_verified_by || ""} onChange={(e) => setMgr((f) => ({ ...f, verbal_verified_by: e.target.value }))}
                        placeholder="Verbal verification completed by" className="h-9 rounded-md border border-stone-300 bg-white px-2.5 text-xs" data-testid={`mgr-verbal-${i}`} />
                    </div>
                    <div className="mt-2 flex justify-end">
                      <Button size="sm" onClick={() => saveMgr(m)} disabled={busy}
                        className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`mgr-save-${i}`}>Save manager's section</Button>
                    </div>
                  </td>
                </tr>
              )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
